import { EnumSideOptions } from "../../sharedTypes";

export interface IMRi {
    headNeck: IHeadNeckOptions;
    spine: EnumSpineOption[];
    extremities: Partial<IExtremitiesOptions>;
    chest: Partial<IChestOptions>;
    abdomen: Partial<IAbdomenOptions>;
    pelvis: IPelvisOptions;
    peripheral: boolean;
    otherMRI: string;
    patientSafetyPoints: IPatientSafetyPoints[];
}

// Head and Neck Options
export enum EnumMRIHeadOption {
    Pituitary = "pituitary",
    Orbits = "orbits",
    Iac = "iac"
}

export enum EnumHeadNeckOthers {
    MraBrain = "mra_brain",
    MrvHead = "mrv_head",
    MriNeck = "mri_neck",
    MraNeck = "mra_neck"
}

interface IHeadNeckOptions {
    mriBrain: IMRIBrain;
    otherOptions: EnumHeadNeckOthers[];
}

interface IMRIBrain {
    options?: EnumMRIHeadOption[];
    note?: string;
}

// Spine Options
export enum EnumSpineOption {
    Cervical = "cervical",
    Thoracic = "thoracic",
    Lumbar = "lumbar",
    LumbosacralPlexus = "lumbosacral_plexus",
    SacralPlexus = "sacral_plexus",
    ScoliosisSurvey = "scoliosis_survey"
}

// Extremities Options
export enum EnumExtremitiesOption {
    Shoulder = "shoulder",
    Elbow = "elbow",
    Forearm = "forearm",
    Wrist = "wrist",
    Hand = "hand",
    Hip = "hip",
    Knee = "knee",
    Ankle = "ankle",
    Forefoot = "forefoot"
}

interface IArthroGramOptions {
    contrast: boolean;
    anesthetic: boolean;
}

interface IAnkleOptions {
    side: EnumSideOptions;
    hasFracture: boolean;
}

interface IForefootOptions {
    side: EnumSideOptions;
    hasBunion: boolean;
}

interface IExtremitiesOptions {
    [EnumExtremitiesOption.Shoulder]: IArthroGramOptions;
    [EnumExtremitiesOption.Elbow]: IArthroGramOptions;
    [EnumExtremitiesOption.Forearm]: EnumSideOptions[];
    [EnumExtremitiesOption.Wrist]: IArthroGramOptions;
    [EnumExtremitiesOption.Hand]: EnumSideOptions[];
    [EnumExtremitiesOption.Hip]: IArthroGramOptions;
    [EnumExtremitiesOption.Knee]: IArthroGramOptions;
    [EnumExtremitiesOption.Ankle]: IAnkleOptions;
    [EnumExtremitiesOption.Forefoot]: IForefootOptions;
}

// Chest Options
export enum EnumChestOptions {
    MraChest = "mra_chest",
    MriBrachialPlexus = "mri_brachial_plexus"
}

interface IChestOptions {
    options: Array<EnumChestOptions>;
    note?: string;
}

// Abdomen Options
export enum EnumAbdomenOption {
    MRILiver = "mri_liver",
    MRIKidneyRenal = "mri_kidney_renal",
    MRIPancreasMRCP = "mri_pancreas_mrcp",
    MRIAbdomenNote = "mri_abdomen_note",
    MRAAbdomenAorta = "mra_abdomen_aorta",
    MRAAbdomenMesenteric = "mra_abdomen_mesenteric",
    MRARenal = "mra_renal"
}

interface IAbdomenOptions {
    options: Partial<Array<EnumAbdomenOption>>;
    [EnumAbdomenOption.MRIAbdomenNote]: string;
}

// Pelvis Options
export enum EnumSoftTissueOption {
    UterusOvaries = "uterus_ovaries",
    Prostate = "prostate",
    Rectal = "rectal"
}

export enum EnumPeripheralOption {
    MRAPeripheral = "mra_peripheral"
}

export enum EnumPelvisOption {
    Bony = "bony",
    SiJoints = "si_joints",
    SoftTissue = "soft_tissue",
    MrvPelvis = "mrv_pelvis",
    SacrumCoccyx = "sacrum_coccyx",
    EnterographyMRE = "enterography_mre",
    Peripheral = "peripheral"
}

interface IPelvisOptions {
    options?: Partial<Array<EnumPelvisOption>>;
    [EnumPelvisOption.EnterographyMRE]?: ISoftTissueOptions;
    [EnumPelvisOption.Peripheral]: IPeripheralOptions;
}

interface ISoftTissueOptions {
    note?: string;
    options?: EnumSoftTissueOption[];
}

interface IPeripheralOptions {
    note?: string;
    options?: EnumPeripheralOption[];
}

// Patient Safety Points
export enum EnumMriSafety {
    AneurysmSurgery = "aneurysm_surgery",
    BrainSurgery = "brain_surgery",
    Pacemaker = "pacemaker",
    CardiacDefibrillator = "cardiac_defibrillator",
    HeartSurgery = "heart_surgery",
    ElectronicImplant = "electronic_implant",
    Neurostimulator = "neurostimulator",
    InsulinPump = "insulin_pump",
    Stents = "stents",
    EyeSurgery = "eye_surgery",
    EarSurgery = "ear_surgery",
    GunshotInjury = "gunshot_injury",
    EyeInjury = "eye_injury",
    EndoscopyCamera = "endoscopy_camera",
    KidneyDisease = "kidney_disease",
    MRIAllergy = "mri_allergy",
    HairPiece = "hair_piece",
    BodyPiercings = "body_piercings",
    SkinPatches = "skin_patches",
    JointReplacement = "joint_replacement",
    BonePin = "bone_pin",
    ProstheticDevice = "prosthetic_device",
    SpineSurgery = "spine_surgery",
    TissueExpanders = "tissue_expanders",
    CancerHistory = "cancer_history",
    Pregnancy = "pregnancy",
    DrugAllergies = "drug_allergies"
}

interface IPatientSafetyPoints {
    question: EnumMriSafety;
    value: boolean | string;
    note?: string;
}
